// StudentOverlay.tsx
import { memo } from 'react';
import type { MouseEvent as ReactMouseEvent, MouseEventHandler } from 'react';
import { useDrag } from 'react-dnd';
import styles from './styles/StudentOverlay.module.css';
import LockUnlockedIcon from './img/lock-unlocked-svgrepo-com.svg';
import LockLockedIcon from './img/lock-keyhole-locked-svgrepo-com.svg';

/** Overlay-Datensatz eines platzierten Studenten im Schedule-Grid (Table.js). */
export interface StudentOverlayData {
  day: number | string;
  studentName: string;
  startTime: string;
  startTimeId: string | number;
  durationMult: number;
  studentId: string | null | undefined;
  memberId: string;
  isAvailable?: boolean;
  isNotAssigned?: boolean;
  isLocked?: boolean;
  split_label?: string | null;
  isFavorite?: boolean;
  [key: string]: unknown;
}

/** react-dnd-Item, das beim Ziehen eines Studenten transportiert wird. */
export interface StudentDragItem {
  cellId: string;
  name: string;
  duration: number;
  member_id: string;
  user_id: string | null | undefined;
}

interface StudentOverlayProps {
  thisStudentOverlay: StudentOverlayData;
  isHighlighted?: boolean;
  onMouseOver?: MouseEventHandler<HTMLDivElement>;
  onMouseOut?: MouseEventHandler<HTMLDivElement>;
  onDelete: (elementId: string) => void;
  onLockToggle?: (memberId: string) => void;
}

function StudentOverlay({thisStudentOverlay, isHighlighted, onMouseOver, onMouseOut, onDelete, onLockToggle }: StudentOverlayProps) {
  const { durationMult, studentName, startTime, day, isAvailable, isNotAssigned, memberId, studentId, isLocked, split_label, isFavorite } = thisStudentOverlay

  const [{ isDragging }, drag] = useDrag({
    type: 'STUDENT',
    item: { cellId: "student_" + day + "_" + startTime, name: studentName, duration: durationMult, member_id: memberId, user_id: studentId } satisfies StudentDragItem,
    canDrag: () => !isLocked,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Base name without split label suffix
  const baseName = split_label ? studentName.replace(` (${split_label})`, '') : studentName
  const splitName = baseName.split(" ")
  const studentDisplayName = splitName.length > 1 ? `${splitName.at(0)} ${splitName.at(-1)}` : baseName

  let titleString = ''
  if (isNotAssigned) titleString += "Student*in ist nicht zu dir eingeteilt\n"
  if (!isAvailable) titleString += "Student*in hat zu dieser Zeit eigentlich keine Zeit"
  if (isLocked) titleString += (titleString ? '\n' : '') + "Student*in ist gelockt (von Berechnung ausgeschlossen)"

  function handleDelete(event: ReactMouseEvent<SVGSVGElement>) {
    event.stopPropagation();
    const closestDivId = (event.target as Element).closest("div")!.id
    onDelete(closestDivId)
  }

  function handleLockToggle(event: ReactMouseEvent<HTMLDivElement>) {
    event.stopPropagation();
    if (onLockToggle) {
      onLockToggle(memberId);
    }
  }

  return (
    <div
      id={memberId}
      ref={(node) => { drag(node); }}
      className={`${styles.studentOverlay} ${isHighlighted ? styles.highlight : ''} ${isNotAssigned ? styles.isNotAssigned : ''} ${isLocked ? styles.locked : ''} ${durationMult <= 2 ? styles.short : ''} ${durationMult === 1 ? styles.singleSlot : ''}`}
      style={{
        height: `calc(${durationMult*100}% - 2px)`,
        opacity: isDragging ? 0.5 : 1,
        cursor: isLocked ? 'not-allowed' : 'grab',
      }}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      title={titleString || studentDisplayName}
    >
      <svg className={styles.deleteButton} height="16" width="16" viewBox="0 0 16 16" onClick={handleDelete}>
        <line x1="4" y1="4" x2="12" y2="12" stroke="#c53030" strokeWidth="2" strokeLinecap="round" />
        <line x1="12" y1="4" x2="4" y2="12" stroke="#c53030" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <div
        className={styles.lockButton}
        onClick={handleLockToggle}
        title={isLocked ? "Student freigeben (für Berechnung freigeben)" : "Student fixieren (von Berechnung ausschließen)"}
      >
        <img
          src={isLocked ? LockLockedIcon : LockUnlockedIcon}
          alt={isLocked ? "Gesperrt" : "Entsperrt"}
          style={{ width: '13px', height: '13px' }}
        />
      </div>
      {(!isAvailable || isNotAssigned) && (
        <div className={styles.warningIcon} title={titleString}>
          ⚠️
        </div>
      )}
      {isFavorite && (
        <div className={styles.favoriteIcon} title="Lieblingszeit">
          ⭐
        </div>
      )}
      <p className={styles.studentOverlayText}>{studentDisplayName}</p>
      {split_label && <span className={styles.splitLabel}>{split_label}</span>}
    </div>
  );
}

export default memo(StudentOverlay);
